import { ViewController } from '@estee/elc-service';
import { observable, computed, action } from 'mobx';
import { StoresDirectoryLoadedUseCase } from '~use-cases/StoresDirectoryLoadedUseCase';
import {
    StoresDirectoryRepository,
    IStoresDirectoryError
} from '~repositories/StoresDirectoryRepository';
import { Config } from '~domain/entities/Config';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { ShowMapClickedUseCase } from '~use-cases/ShowMapClickedUseCase';
import { events, emit } from '@estee/elc-service-bus';

export class StoresDirectoryViewController extends ViewController {
    @observable private storesDirectoryRepository: StoresDirectoryRepository;
    @observable private errCode: number;
    @observable public configService: Config;

    constructor(storesDirectoryRepository: StoresDirectoryRepository) {
        super();
        this.storesDirectoryRepository = storesDirectoryRepository;
    }

    @action
    public loaded = async () => {
        this.isLoading = true;
        this.configService = diContainer.get(serviceNames.config);
        await new StoresDirectoryLoadedUseCase().execute();
        if (this.storesDirectoryRepository.storesDirectoryError) {
            this.handleStoresDirectoryError(this.storesDirectoryRepository.storesDirectoryError);
        }
        this.isLoading = false;
    };

    @action
    public onShowMapClicked = () => {
        new ShowMapClickedUseCase().execute();
        emit(events.STORE_DIRECTORY_MAP_LINK_CLICKED, {
            eventName: 'store_finder',
            eventCategory: 'store_directory_click',
            eventAction: 'stores',
            eventLabel: 'stores'
        });
    };

    @action
    public onStoreDirectoryLinkClicked = (storesname: string) => {
        emit(events.STORE_DIRECTORY_STORE_LINK_CLICKED, {
            eventName: 'store_directory',
            eventCategory: 'store_directory_click',
            eventAction: storesname,
            eventLabel: storesname
        });
    };

    @computed
    get stores() {
        return this.storesDirectoryRepository.stores;
    }

    @computed
    get getCountry() {
        const storeInfo = this.storesDirectoryRepository.stores[0]?.stores?.[0]?.address;
        const country = storeInfo?.country ?? '';

        return country;
    }

    @computed
    get sortedStoreGroupsByState() {
        return [...this.storesDirectoryRepository.stores].sort((a, b) => {
            const stateA = a.stores[0].address.state || '';
            const stateB = b.stores[0].address.state || '';

            return stateA.localeCompare(stateB);
        });
    }

    @computed
    get data() {
        return {
            stores: this.stores,
            areStoresDirectoryItemsLoading: this.isLoading,
            country: this.getCountry,
            config: this.configService && this.configService.config,
            errorCode: this.errorCode
        };
    }

    @action setErrorCode(errCode: number) {
        this.errCode = errCode;
    }

    @computed
    public get errorCode(): number {
        return this.errCode;
    }

    private handleStoresDirectoryError(error: IStoresDirectoryError) {
        this.setErrorCode(error.status);
    }
}
